/**
 * @file The `lc>` brand mark — Chakra-Petch `lc` text + pure-SVG
 *       chevron caret that doubles as a `D`. Self-contained, sizes
 *       from a single `capH` knob, theme-reactive by default.
 * @module @labcraft/ui/chrome/labcraft-logo
 *
 * The mark spells out **lab · craft · dev** in shell-prompt form. The
 * caret is the load-bearing idea: a stroked chevron whose pointed
 * bowl reads as a `D` — never substitute a font `>` glyph.
 *
 * Sizing: every dimension derives from `capH` (the `lc` letters'
 * cap height in CSS px). Scale the mark by changing `capH` alone.
 *
 * Theming: by default `lc` paints in `--text-primary` and the caret
 * in `--text-accent`, both of which auto-flip with the document
 * `[data-theme]` cascade — light = ink/vivid, dark = wash/light. To
 * force a specific colour (mono context, on-tile composition), pass
 * `tone="mono"` and an explicit `color`.
 */

import type { CSSProperties, JSX } from 'react'
import styles from './labcraft_logo.module.css'

/**
 * Props for {@link LabCraftLogo}.
 *
 * @property capH - Cap height of the `lc` letters, in CSS px. Every
 *   other dimension derives from this. Defaults to 48.
 * @property tone - Colour tone. `"theme"` (default) reads
 *   `--text-primary` / `--text-accent` and tracks the document
 *   `[data-theme]` automatically. `"mono"` paints both parts in
 *   {@link LabCraftLogoProps.color | `color`} (defaults to ink) and
 *   is for backgrounds where the theme cascade doesn't reach (avatars,
 *   on-tile composition, exported assets).
 * @property color - Required when `tone === "mono"`. Single ink applied
 *   to both `lc` and the caret. Accepts any CSS colour value.
 * @property className - Pass-through for callers that want to wrap the
 *   mark in a host-specific layout selector (sizing parent, animation
 *   anchor, anchor link). The component's own root class is always
 *   applied alongside.
 * @property ariaLabel - Accessible label for the link/button context
 *   the mark sits inside. When omitted the mark is `aria-hidden`,
 *   which is the right call when the mark sits next to its own visible
 *   wordmark or alt text.
 */
export interface LabCraftLogoProps {
  capH?: number
  tone?: 'theme' | 'mono'
  color?: string
  className?: string
  ariaLabel?: string
}

/**
 * Render the `lc>` brand mark.
 *
 * @param props - See {@link LabCraftLogoProps}.
 * @returns A `<span>` carrying the live-text `lc` plus the inline
 *   chevron caret SVG.
 */
export function LabCraftLogo(props: LabCraftLogoProps): JSX.Element {
  const capH = props.capH ?? 48
  const tone = props.tone ?? 'theme'
  const monoColor = props.color ?? '#26215C'

  const rootStyle: CSSProperties = {
    ['--lc-cap' as string]: `${capH}px`,
    ...(tone === 'mono'
      ? {
          ['--lc-mark-text' as string]: monoColor,
          ['--lc-mark-caret' as string]: monoColor,
        }
      : {}),
  }

  const rootClassName = props.className
    ? `${styles.root} ${props.className}`
    : styles.root

  return (
    <span
      className={rootClassName}
      style={rootStyle}
      aria-label={props.ariaLabel}
      aria-hidden={props.ariaLabel ? undefined : true}
      role={props.ariaLabel ? 'img' : undefined}>
      <span className={styles.text}>lc</span>
      <svg
        viewBox="0 0 56 100"
        className={styles.caret}
        fill="none"
        aria-hidden="true">
        <path
          d="M13 14 L45 50 L13 86"
          fill="none"
          stroke="currentColor"
          strokeWidth="18"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}
